import { ASSET_CLASS_PAGE_TO_LABEL } from "~/modules/asset_classes/metadata";
import type { SearchResultItem } from "../types";
import { REPRESENTED_ASSETS_CATEGORY } from "./search-categories";

// // // //

const CATEGORIES_BEFORE_STOCKS = new Set([
    ASSET_CLASS_PAGE_TO_LABEL.stablecoins,
    ASSET_CLASS_PAGE_TO_LABEL.treasuries,
    ASSET_CLASS_PAGE_TO_LABEL.government_bonds,
    ASSET_CLASS_PAGE_TO_LABEL.credit,
    ASSET_CLASS_PAGE_TO_LABEL.pe_vc,
    ASSET_CLASS_PAGE_TO_LABEL.active_strategies,
    ASSET_CLASS_PAGE_TO_LABEL.commodities,
    ASSET_CLASS_PAGE_TO_LABEL.real_estate,
    "Networks",
    "Platforms",
    "Asset Managers",
]);

/**
 * Group search results by asset class page, sorted by match count.
 */
export function groupSearchResults(
    items: SearchResultItem[],
    query: string,
): Record<string, SearchResultItem[]> {
    // Initialize the grouped object
    const grouped: Record<string, SearchResultItem[]> = {};

    // Group the items by category
    for (const item of items) {
        (grouped[item.category] ??= []).push(item);
    }

    // Sort the entries by match count
    const entries = Object.entries(grouped);
    if (query.trim() !== "") {
        entries.sort(([, aItems], [, bItems]) => bItems.length - aItems.length);
    }

    // Stocks follows the other asset and entity sections, even when its match
    // count is larger. With no query, this places it just after Asset Managers.
    // Preserve the relative order of all other sections.
    const stocksIndex = entries.findIndex(
        ([category]) => category === ASSET_CLASS_PAGE_TO_LABEL.stocks,
    );

    // Find the last priority index
    const lastPriorityIndex = entries.reduce(
        (lastIndex, [category], index) =>
            CATEGORIES_BEFORE_STOCKS.has(category) ? index : lastIndex,
        -1,
    );

    // Move stocks to the last priority index if it exists and is before the last priority index
    if (stocksIndex >= 0 && stocksIndex < lastPriorityIndex) {
        const [stocks] = entries.splice(stocksIndex, 1);
        entries.splice(lastPriorityIndex, 0, stocks!);
    }

    // Represented assets are always the final section, including after catalog
    // results, regardless of match count or the order received from the API.
    const representedIndex = entries.findIndex(
        ([category]) => category === REPRESENTED_ASSETS_CATEGORY,
    );
    if (representedIndex >= 0) {
        const [represented] = entries.splice(representedIndex, 1);
        entries.push(represented!);
    }

    return Object.fromEntries(entries);
}
